import * as React from "react"
import { cn } from "@/lib/utils"

type CardProps = React.HTMLAttributes<HTMLDivElement> & {
  variant?: "default" | "highlight"
  accent?: "green" | "blue" | "orange" | "purple"
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = "default", accent = "green", ...props }, ref) => {
    const accentMap: Record<string, string> = {
      green: "from-brand-tile-success to-green-50 border-emerald-200",
      blue: "from-brand-tile-info to-sky-50 border-sky-200",
      orange: "from-brand-tile-warning to-amber-50 border-amber-200",
      purple: "from-brand-tile-accent to-violet-50 border-violet-200",
    }

    const variantClass =
      variant === "highlight"
        ? `bg-gradient-to-br ${accentMap[accent]} border rounded-2xl`
        : "rounded-2xl border bg-card text-card-foreground"

    return (
      <div
        ref={ref}
        className={cn(
          variantClass,
          "shadow-sm transition-all duration-200 hover:shadow-md",
          className
        )}
        {...props}
      />
    )
  }
)
Card.displayName = "Card"

/* ──────────────────────────────
   Header / Title / Description
──────────────────────────────── */

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      // ↓ Reduced from text-2xl to text-lg/md:text-xl for better hierarchy
      "text-lg md:text-xl font-semibold leading-snug text-brand-text flex items-center gap-3",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "text-sm md:text-base text-slate-600 leading-relaxed",
      className
    )}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
}
